import React from 'react';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {HistoryState, IndexSlice} from 'notebook/ELNConstants';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {CidIdMap, PRINT_OPTION} from 'notebook/notes/NoteConstants';
import {NoteView} from 'notebook/notes/tabs/NoteViews';
import {Review} from 'notebook/reviews/ReviewConstants';
import {StageEntryReviewBadge} from 'notebook/reviews/StageEntryReviewBadge';
import HistoryManager from 'notebook/store/HistoryManager';
import {EntryNote, Note} from 'notebook/util/NoteUtilConstants';
import licenses from 'user/Licenses';
import {assertNever} from 'util/Types';
import {StageRun, StageRunSample} from 'workflows/WorkflowConstants';

import GetStageRunsWrapper from './components/GetStageRunsWrapper';
import InputTable from './components/InputTable';
import OutputTable from './components/OutputTable';
import * as StageEntryActions from './StageEntryActions';
import StageEntryStore from './StageEntryStore';
import * as StageEntryUtil from './StageEntryUtil';

interface NoteViewContentProps extends StageEntryNoteViewProps {
  isExportView: boolean | undefined;
  isLocked: boolean;
  getIdFromCid: (cid: string) => string | number | undefined;
  _getLatestNote: () => EntryNote | undefined;
  printHandler: (type: PRINT_OPTION, indexSlice: IndexSlice) => void;
  registerObjects: (cidIdMap: CidIdMap) => void;
  updateNote: (note: EntryNote) => void;
  updateTitle: (name: string) => void;
  historyManager: HistoryManager<HistoryState<Note>>;
  restoreStateAndSelection: (state: HistoryState<EntryNote>, selectionData: SelectionRestoreData) => void;
  titleBadge: React.ReactNode;
}

const NoteViewContent: React.FC<NoteViewContentProps> = ({
  isExportView,
  isLocked,
  getIdFromCid,
  _getLatestNote,
  printHandler,
  registerObjects,
  updateNote,
  updateTitle,
  historyManager,
  restoreStateAndSelection,
  titleBadge,
  indexSlice,
  beforeEntry,
  fileId,
  note,
  viewer,
  ...otherProps
}) => {
  // Hide input and output tables if user is printing a selection
  const isViewingSelection = indexSlice && (indexSlice.start > 0 || indexSlice.end < Infinity);

  return (
    <NoteView
      {...otherProps}
      beforeEntry={
        <>
          {beforeEntry}

          {isViewingSelection ? null : <InputTable stageEntryId={fileId} locked={isLocked} viewer={viewer} />}
        </>
      }
      afterEntry={
        isViewingSelection ? null : (
          <GetStageRunsWrapper entryId={fileId}>
            {(stageRuns: Array<StageRun>, stageRunSampleById: {[id: string]: StageRunSample}) => (
              <OutputTable
                note={note}
                stageEntryId={fileId}
                stageRuns={stageRuns}
                stageRunSampleById={stageRunSampleById}
                locked={isLocked}
                viewer={viewer}
              />
            )}
          </GetStageRunsWrapper>
        )
      }
      getIdFromCid={getIdFromCid}
      _getLatestNote={_getLatestNote}
      printHandler={printHandler}
      registerObjects={registerObjects}
      updateNote={updateNote}
      updateTitle={updateTitle}
      historyManager={historyManager}
      locked={isLocked}
      restoreStateAndSelection={restoreStateAndSelection}
      // Replicating here the logic of PrinterFriendlyView: if <NoteEditorContainer> gets rendered in
      // PrinterFriendlyView then titleBadge prop is not passed down to NodeEditor.
      titleBadge={isExportView ? null : titleBadge}
      indexSlice={indexSlice}
      note={note}
      fileId={fileId}
      viewer={viewer}
    />
  );
};

// TODO(TypeScript): share subset with NoteViewProps once NoteView is in TS
export interface StageEntryNoteViewProps {
  beforeEntry?: React.ReactNode;
  fileId: string;
  humanId?: string | null;
  isLatest: boolean;
  allowEdits: boolean;
  note: Note;
  review?: Review;
  viewer: boolean;
  indexSlice: IndexSlice;
  permissions: Permissions;
  lockedFromExternalChange?: boolean;
  fileType: string;
  parentType: string;
  title: string;
  showTimestamps: boolean;
  disableFormatting: boolean;
  hideTitle: boolean;
  locked: boolean;
  preview: {showOpenLink: boolean};
  restoreState: () => void;
  storeState: () => void;
  printOptions: Array<PRINT_OPTION>;
  printLayout: boolean;
  showPrintMargins: boolean;
  spellCheck: boolean;
  showTableOfContents: boolean;
}

interface State {
  historyManager: HistoryManager<HistoryState<Note>>;
}

export default class StageEntryNoteView extends React.PureComponent<StageEntryNoteViewProps, State> {
  static defaultProps = {
    viewer: false,
  };

  override state: State = {
    historyManager: new HistoryManager(),
  };

  getIdFromCid = (cid: string): string | number | undefined =>
    StageEntryStore.getIdFromCid(this.props.fileId, cid);

  _getLatestNote = (): EntryNote | undefined =>
    StageEntryStore.getEntry(this.props.fileId)?.entryVersion.note;

  printHandler = (type: PRINT_OPTION, indexSlice: IndexSlice): void => {
    switch (type) {
      case PRINT_OPTION.ENTRY:
        StageEntryActions.print(this.props.fileId, {});
        break;
      case PRINT_OPTION.NOTE:
        StageEntryActions.print(this.props.fileId, {protocolCids: []});
        break;
      case PRINT_OPTION.SELECTION:
        StageEntryActions.print(this.props.fileId, {protocolCids: [], indexSlice});
        break;
      case PRINT_OPTION.TEMPLATE:
        assert(type !== PRINT_OPTION.TEMPLATE, 'Stage Entries do not have template print options');
        break;
      default:
        throw new Error(`Unexpected print handler type ${assertNever(type)}`);
    }
  };

  restoreStateAndSelection = (
    {note, title}: HistoryState<EntryNote>,
    selectionData: SelectionRestoreData
  ): void => {
    StageEntryActions.revert(
      this.props.fileId,
      {
        name: {$set: title},
        ...StageEntryUtil.entryVersionUpdate({$merge: {note}}),
      },
      selectionData
    );
  };

  updateNote = (note: EntryNote): void => {
    StageEntryActions.updateNote(this.props.fileId, note);
  };

  registerObjects = (cidIdMap: CidIdMap): void => {
    StageEntryActions.registerObjects(this.props.fileId, cidIdMap);
  };

  updateTitle = (name: string): void => {
    StageEntryActions.setName(this.props.fileId, name);
  };

  isLocked(): boolean {
    return !this.props.isLatest || !this.props.allowEdits;
  }

  _getTitleBadge(): React.ReactNode {
    if (licenses.hasEnterprisePlan()) {
      return <StageEntryReviewBadge className="noteEditor-reviewBadge" review={this.props.review} />;
    }
    return null;
  }

  override render(): JSX.Element {
    return (
      <EditorPropertiesContext.Consumer>
        {({isExportView}) => (
          <NoteViewContent
            {...this.props}
            isExportView={isExportView}
            isLocked={this.isLocked()}
            getIdFromCid={this.getIdFromCid}
            _getLatestNote={this._getLatestNote}
            printHandler={this.printHandler}
            registerObjects={this.registerObjects}
            updateNote={this.updateNote}
            updateTitle={this.updateTitle}
            historyManager={this.state.historyManager}
            restoreStateAndSelection={this.restoreStateAndSelection}
            titleBadge={this._getTitleBadge()}
          />
        )}
      </EditorPropertiesContext.Consumer>
    );
  }
}
